


import { useState, useEffect } from "react";
import FoodOverviewTab from "./FoodOverviewTab";
import RevenueTab from "./RevenueTab";
import FoodOrdersTab from "./FoodOrdersTab.jsx";
import FoodPaymentsTab from "./FoodPaymentsTab.jsx";
import FoodFinancialsTab from "./FoodFinancialsTab.jsx";
import FoodRatingsTab from "./FoodRatingsTab.jsx";
import FoodReportsTab from "./FoodReportsTab";
import GlobalFilters from "./GlobalFilter";
import { Menu, X } from "lucide-react";
import "./ReportAnalysisTab.css";

import {
    BarChart2, TrendingUp, ShoppingCart, CreditCard, IndianRupee, Star, FileText, Store,
} from "lucide-react";

// Define all available tabs with their submodule mapping
const ALL_TABS = [
    { id: "overview", label: "Overview", icon: BarChart2, subModuleKey: "REPORTS_OVERVIEW" },
    { id: "revenue", label: "Revenue", icon: TrendingUp, subModuleKey: "REPORTS_REVENUE" },
    { id: "orders", label: "Orders", icon: ShoppingCart, subModuleKey: "REPORTS_ORDERS" },
    { id: "ratings", label: "Ratings", icon: Star, subModuleKey: "REVIEWS_RATINGS" },
];

function Foodreports({ filteredData: propFilteredData, isLoading: propIsLoading }) {  // ✅ Accept props
    const [activeTab, setActiveTab] = useState(null);
    const [tabs, setTabs] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // States for filtered data
    // const [filteredData, setFilteredData] = useState(null);
    // const [isLoading, setIsLoading] = useState(false);


    const [localFilteredData, setLocalFilteredData] = useState(null);
    const [localIsLoading, setIsLoading] = useState(false);



    // Get sidebar state from localStorage
    useEffect(() => {
        const checkSidebarState = () => {
            const savedState = localStorage.getItem("sidebarOpen");
            if (savedState !== null) {
                setSidebarOpen(JSON.parse(savedState));
            }
        };

        checkSidebarState();

        const handleStorageChange = (e) => {
            if (e.key === "sidebarOpen") {
                setSidebarOpen(JSON.parse(e.newValue));
            }
        };

        const handleSidebarToggle = (e) => {
            setSidebarOpen(e.detail.isOpen);
        };

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            if (window.innerWidth > 768) {
                setMobileFiltersOpen(false);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('sidebarToggle', handleSidebarToggle);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('sidebarToggle', handleSidebarToggle);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Filter tabs based on user role
    useEffect(() => {
        // Get user role and subModules from localStorage
        const userRole = localStorage.getItem("role");
        const subModules = JSON.parse(localStorage.getItem("subModules") || "[]");

        let availableTabs = [];

        // Check if user is employee
        if (userRole === "ROLE_EMPLOYEE") {
            // Filter tabs based on subModules from localStorage
            availableTabs = ALL_TABS.filter(tab =>
                subModules.includes(tab.subModuleKey)
            );
        } else {
            // For vendors, show all tabs (no restrictions)
            availableTabs = [...ALL_TABS];
        }

        setTabs(availableTabs);

        // Set active tab to first available tab if exists
        if (availableTabs.length > 0 && activeTab === null) {
            setActiveTab(availableTabs[0].id);
        }
    }, []); // Only runs once on mount

    // Filter handlers
    const handleFilterChange = (data) => {
        setLocalFilteredData(data);
        setIsLoading(false);
    };

    const handleLoadingChange = (loading) => {
        setIsLoading(loading);
    };

    // Determine if we're in the 700-1111px range
    const isInCustomRange = windowWidth >= 700 && windowWidth <= 1111;
    const isMobile = windowWidth < 768;
    // Use prop data if provided, otherwise use local state
    const filteredData = propFilteredData !== undefined ? propFilteredData : localFilteredData;
    const isLoading = propIsLoading !== undefined ? propIsLoading : localIsLoading;
    const renderTab = () => {

        const tabProps = {
            data: filteredData,      // ✅ Pass filtered data to child tabs
            isLoading: isLoading     // ✅ Pass loading state
        };

        switch (activeTab) {

            case "overview":
                return <FoodOverviewTab {...tabProps} />;

            case "revenue":
                return <RevenueTab {...tabProps} />;

            case "orders":
                return <FoodOrdersTab {...tabProps} />;

            case "ratings":
                return <FoodRatingsTab {...tabProps} />;

            default:
                return <FoodOverviewTab {...tabProps} />;
        }
    };

    // Dynamic className based on sidebar state and screen width
    const getDashboardClassName = () => {
        let className = "food-analysis-dashboard min-h-screen bg-dash";

        className += sidebarOpen ? ' sidebar-open' : ' sidebar-closed';

        if (isInCustomRange) {
            className += ' custom-range';
        }

        if (sidebarOpen && isInCustomRange) {
            className += ' sidebar-open-custom-range';
        }

        return className;
    };

    // If no tabs available, show a message
    if (tabs.length === 0) {
        return (
            <div className="food-analysis-dashboard min-h-screen bg-dash">
                <div style={{
                    textAlign: "center",
                    padding: "50px",
                    color: "#64748b"
                }}>
                    <h3>No modules available</h3>
                    <p>You don't have access to any modules in this section.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={getDashboardClassName()}>
            {/* Header */}


            {/* Navigation Tabs */}
            <div className="bg-card border-b border-border sticky top-[var(--header-height)] z-20">
                <div className="mx-auto px-4" style={{ paddingBottom: "14px", borderBottom: "2px solid #e66d33", overflow: "auto", width: window.innerWidth < 769 ? "100vw" : " ", scrollbarWidth: "none" }}>
                    <nav className="flex overflow-x-auto scrollbar-hide gap-0">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const active = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 whitespace-nowrap transition-all duration-200 ${active
                                        ? "border-primary text-primary"
                                        : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                                        }`}
                                >
                                    <Icon size={15} />
                                    <span>{tab.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <main className="mx-auto p-4 space-y-5" style={{ height: window.innerHeight < 400 ? "55vh" : window.innerHeight < 500 ? "60vh" : window.innerHeight < 600 ? "65vh" : "75vh", overflow: "auto", scrollbarWidth: "none" }}>
                {renderTab()}
            </main>
        </div>
    );
} export default Foodreports;